import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { PILLARS, ALL_PILLAR_KEYS, normalizeNutrientKey } from '@/shared/constants/metabolic-pillars';
import { Event } from '@/types/zod.schemas';
import { toLogicDate } from '@/utils/date-utils';

/**
 * NUTRITION COMPUTE: Pure functions for macro/micro aggregation and ledger management.
 * V8.4: ABSOLUTE SINGLE FACT & ALIAS SYNC.
 * Handles the {amount, unit} structure and normalizes all synonyms (e.g. Niacin/B3).
 */

export interface SourceData {
    label: string;
    amount: number;
    type: 'FOOD' | 'SUPPLEMENT';
}

export interface DetailedMetric {
    total: number;
    unit?: string;
    sources: SourceData[];
}

export interface DetailedMicros {
    [key: string]: DetailedMetric;
}

export interface DetailedTotals {
    calories: DetailedMetric;
    protein: DetailedMetric;
    carbs: DetailedMetric;
    fat: DetailedMetric;
    insulin_load: DetailedMetric;
    micros: DetailedMicros;
}

export const NutritionCompute = {
    normalizeLedgerItems(payload: any): any[] {
        if (!payload) return [];
        if (payload.items && Array.isArray(payload.items)) {
            return payload.items.map((it: any, idx: number) => ({
                ...it,
                id: it.id || `item-${idx}`
            }));
        }
        // Legacy fallback
        if (payload.calories || payload.protein) {
            return [{
                id: 'legacy-entry',
                item_name: payload.meal_name || 'Legacy Entry',
                calories: payload.calories || 0,
                protein: payload.protein || 0,
                carbs: payload.carbs || 0,
                fat: payload.fat || 0,
                insulin_load: payload.insulin_load || 0
            }];
        }
        return [];
    },

    createMetric(total = 0, unit = 'mg'): DetailedMetric {
        return { total, unit, sources: [] };
    },

    /**
     * resolveValueAndUnit: Standardizes data extraction for V9 Tuples and DetailedMetrics.
     */
    resolveValueAndUnit(val: any, defaultUnit = 'mg'): { amount: number, unit: string } {
        if (val === undefined || val === null) return { amount: 0, unit: defaultUnit };
        
        if (typeof val === 'object' && 'total' in val) {
            return { amount: Number(val.total) || 0, unit: val.unit || defaultUnit };
        }
        
        if (typeof val === 'object' && 'amount' in val) {
            return { amount: Number(val.amount) || 0, unit: val.unit || defaultUnit };
        }

        if (typeof val === 'string') {
            const numericPart = val.replace(/[^0-9.]/g, '');
            const stringUnit = val.replace(/[0-9.]/g, '').trim() || defaultUnit;
            return { amount: parseFloat(numericPart) || 0, unit: stringUnit };
        }
        
        return { amount: Number(val) || 0, unit: defaultUnit };
    },

    /**
     * calculateLedgerTotals: Unified aggregation engine.
     * V8.4: ALIAS NORMALIZED.
     */
    calculateLedgerTotals(items: any[]): DetailedTotals {
        const acc: DetailedTotals = {
            calories: this.createMetric(0, 'kcal'),
            protein: this.createMetric(0, 'g'),
            carbs: this.createMetric(0, 'g'),
            fat: this.createMetric(0, 'g'),
            insulin_load: this.createMetric(0, ''),
            micros: {}
        };

        items.forEach(curr => {
            const label = curr.item_name || 'Unlabeled Item';
            const type = curr.type === 'SUPPLEMENT' ? 'SUPPLEMENT' : 'FOOD';
            
            let baseAmount = Number(curr.dosage_count || curr.quantity || 1) || 1;
            let multiplier = baseAmount;

            if (type === 'SUPPLEMENT' && curr.measure_unit && ['g', 'ml'].includes(curr.measure_unit)) {
                const scoopWeight = Number(curr.manufacturer_scoop_weight || curr.catalog_item?.manufacturer_scoop_weight || 0);
                if (scoopWeight > 0) {
                    multiplier = baseAmount / scoopWeight;
                }
            }

            ['calories', 'protein', 'carbs', 'fat', 'insulin_load'].forEach(m => {
                const { amount } = this.resolveValueAndUnit(curr[m], m === 'calories' ? 'kcal' : 'g');
                const calculatedVal = amount * (type === 'FOOD' ? 1 : multiplier);
                if (calculatedVal > 0) {
                    const k = m as keyof DetailedTotals;
                    (acc[k] as DetailedMetric).total += calculatedVal;
                    (acc[k] as DetailedMetric).sources.push({ label, amount: calculatedVal, type });
                }
            });

            const m = curr.baseline_micros || curr.micros || {};
            Object.entries(m).forEach(([key, val]: [string, any]) => {
                if (['id', 'item_name', 'quantity', 'unit', 'numeric_amount', 'discovered_micros', 'detailed_metrics'].includes(key)) return;

                const { amount, unit } = this.resolveValueAndUnit(val);
                if (amount <= 0) return;

                const calculatedAmount = amount * multiplier;
                const canonicalKey = normalizeNutrientKey(key);

                if (!acc.micros[canonicalKey]) acc.micros[canonicalKey] = this.createMetric(0, unit);
                acc.micros[canonicalKey].total += calculatedAmount;
                acc.micros[canonicalKey].sources.push({ label, amount: calculatedAmount, type });
            });
        });

        return acc;
    },

    sanitizeMacroValue(field: string, value: any): number | string {
        if (field === 'insulin_spike_risk' || field === 'item_name') return value;
        const num = parseFloat(value);
        if (isNaN(num)) return 0;
        return field === 'calories' || field === 'glycemic_index' ? Math.round(num) : Number(num.toFixed(1));
    },

    resolveMetricValue(val: any): number {
        return this.resolveValueAndUnit(val).amount;
    },

    getTacticalNutritionTip(totals: DetailedTotals): string | null {
        if (totals.insulin_load.total > 50) return "High Insulin Load detected. Adding 15g of healthy fats or fiber will blunt this spike by ~20%.";
        if (totals.carbs.total > 100 && totals.protein.total < 20) return "Carb-dominant meal. Adding protein will improve nutrient partitioning and slow digestion.";
        return null;
    },

    flattenPayloadForDB(payload: any): any {
        if (!payload) return {};
        const clean = { ...payload };
        
        // V9.0 PURGE: NO precalculated arrays in the DB payloads to ensure JIT truth.
        delete clean.active_ingredients;
        delete clean.discovered_micros;
        delete clean.detailed_metrics;
        delete clean.micros; 
        
        if (payload.items) {
            const freshTotals = this.calculateLedgerTotals(payload.items);
            ['calories', 'protein', 'carbs', 'fat', 'insulin_load'].forEach(key => {
                const k = key as keyof DetailedTotals;
                clean[key] = (freshTotals[k] as DetailedMetric).total;
            });
        }
        
        return clean;
    },

    calculateDynamicTargets(profile: any, trainingBurn: number, caloriesConsumed: number) {
        const weight = 85; 
        const sensitivityFactor = profile?.insulin_sensitivity === 'LOW' ? 0.7 : (profile?.insulin_sensitivity === 'HIGH' ? 1.3 : 1.0);
        const goal = profile?.primary_goal || 'MAINTENANCE';
        let calorieModifier = 0;
        if (goal === 'FAT_LOSS') calorieModifier = -500;
        if (goal === 'MUSCLE_GAIN') calorieModifier = 300;
        let proteinRatio = trainingBurn > 300 ? 2.0 : 1.6;
        if (goal === 'FAT_LOSS') proteinRatio = 2.3;
        else if (goal === 'RECOMPOSITION') proteinRatio = 2.2;
        const proteinTarget = Math.round(weight * proteinRatio);
        const fiberRatio = goal === 'FAT_LOSS' ? 16 : 14;
        const fiberTarget = Math.max(30, Math.round((caloriesConsumed / 1000) * fiberRatio));
        const loadTarget = Math.round(150 * sensitivityFactor);
        const sweatMultiplier = 1 + (trainingBurn / 1000); 
        return {
            macros: { protein: proteinTarget, fiber: fiberTarget, insulin_load: loadTarget, calorie_modifier: calorieModifier },
            micros: { leucine: Math.round(weight * 40), glycine: 5000, zinc: profile?.gender === 'MALE' ? 11 : 8, sodium: Math.round(2300 * sweatMultiplier), potassium: 3500, magnesium: Math.round(400 * sweatMultiplier), omega_3: 1000, iron: profile?.gender === 'FEMALE' ? 18 : 8, vitamin_b12: 2.4, vitamin_d: 1000, caffeine: 400, iodine: 150, creatine: 5000, citrulline: 6000, ashwagandha: 600, tyrosine: 1000, melatonin: 1, calcium: 1000, glutamine: 5000, vitamin_c: 500, hyaluronic_acid: 120, collagen: 10000 }
        };
    },

    /**
     * aggregateDayNutrition: High-level daily rollup for NUTRITION only.
     */
    aggregateDayNutrition(events: any[], dateStr: string): DetailedTotals {
        const acc: DetailedTotals = { 
            calories: this.createMetric(0, 'kcal'), 
            protein: this.createMetric(0, 'g'), 
            carbs: this.createMetric(0, 'g'), 
            fat: this.createMetric(0, 'g'), 
            insulin_load: this.createMetric(0, ''), 
            micros: {} 
        };

        const dayEvents = events.filter(e => toLogicDate(e.start_time) === dateStr && e.status === 'COMPLETED' && e.type === 'NUTRITION');

        dayEvents.forEach(curr => {
            const payload = (curr.payload as any) || {};
            const items = payload.items || [];
            
            if (items.length > 0) {
                const eventTotals = this.calculateLedgerTotals(items);
                ['calories', 'protein', 'carbs', 'fat', 'insulin_load'].forEach(key => {
                    const k = key as keyof DetailedTotals;
                    (acc[k] as DetailedMetric).total += (eventTotals[k] as DetailedMetric).total;
                    (acc[k] as DetailedMetric).sources.push(...(eventTotals[k] as DetailedMetric).sources);
                });
                Object.keys(eventTotals.micros).forEach(key => {
                    if (!acc.micros[key]) acc.micros[key] = this.createMetric(0, eventTotals.micros[key].unit);
                    acc.micros[key].total += eventTotals.micros[key].total;
                    acc.micros[key].sources.push(...eventTotals.micros[key].sources);
                });
            } else if (payload.calories || payload.protein) {
                ['calories', 'protein', 'carbs', 'fat', 'insulin_load'].forEach(key => {
                    const k = key as keyof DetailedTotals;
                    const val = Number(payload[k]) || 0;
                    if (val > 0) {
                        (acc[k] as DetailedMetric).total += val;
                        (acc[k] as DetailedMetric).sources.push({ label: curr.label || 'Legacy Log', amount: val, type: 'FOOD' });
                    }
                });
                
                const m = payload.micros || {};
                Object.keys(m).forEach(key => {
                    const canonicalKey = normalizeNutrientKey(key);
                    const { amount, unit } = this.resolveValueAndUnit(m[key]);
                    if (amount > 0) {
                        if (!acc.micros[canonicalKey]) acc.micros[canonicalKey] = this.createMetric(0, unit);
                        acc.micros[canonicalKey].total += amount;
                        acc.micros[canonicalKey].sources.push({ label: curr.label || 'Legacy Log', amount, type: 'FOOD' });
                    }
                });
            }
        });

        return acc;
    },

    /**
     * aggregateDaySupplements: High-level daily rollup for SUPPLEMENTS only.
     */
    aggregateDaySupplements(events: any[], dateStr: string): DetailedTotals {
        const acc: DetailedTotals = { 
            calories: this.createMetric(0, 'kcal'), 
            protein: this.createMetric(0, 'g'), 
            carbs: this.createMetric(0, 'g'), 
            fat: this.createMetric(0, 'g'), 
            insulin_load: this.createMetric(0, ''), 
            micros: {} 
        };

        const dayEvents = events.filter(e => toLogicDate(e.start_time) === dateStr && e.status === 'COMPLETED' && (e.type === 'SUPPLEMENT' || e.type === 'SUPPLEMENTATION'));

        dayEvents.forEach(curr => {
            const payload = (curr.payload as any) || {};
            const items = payload.items || [];
            
            if (items.length > 0) {
                const eventTotals = this.calculateLedgerTotals(items);
                Object.keys(eventTotals.micros).forEach(key => {
                    if (!acc.micros[key]) acc.micros[key] = this.createMetric(0, eventTotals.micros[key].unit);
                    acc.micros[key].total += eventTotals.micros[key].total;
                    acc.micros[key].sources.push(...eventTotals.micros[key].sources);
                });
            }
        });

        return acc;
    },

    /**
     * mergeMetabolicLoad: Combines nutrition and supplement totals.
     */
    mergeMetabolicLoad(nutrition: DetailedTotals, supplements: DetailedTotals): DetailedTotals {
        const combined: DetailedTotals = {
            calories: { total: nutrition.calories.total + supplements.calories.total, unit: 'kcal', sources: [...nutrition.calories.sources, ...supplements.calories.sources] },
            protein: { total: nutrition.protein.total + supplements.protein.total, unit: 'g', sources: [...nutrition.protein.sources, ...supplements.protein.sources] },
            carbs: { total: nutrition.carbs.total + supplements.carbs.total, unit: 'g', sources: [...nutrition.carbs.sources, ...supplements.carbs.sources] },
            fat: { total: nutrition.fat.total + supplements.fat.total, unit: 'g', sources: [...nutrition.fat.sources, ...supplements.fat.sources] },
            insulin_load: { total: nutrition.insulin_load.total + supplements.insulin_load.total, unit: '', sources: [...nutrition.insulin_load.sources, ...supplements.insulin_load.sources] },
            micros: {}
        };

        const allMicroKeys = new Set([...Object.keys(nutrition.micros), ...Object.keys(supplements.micros)]);
        allMicroKeys.forEach(key => {
            const nut = nutrition.micros[key];
            const sup = supplements.micros[key];
            const unit = nut?.unit || sup?.unit || 'mg';
            combined.micros[key] = {
                total: (nut?.total || 0) + (sup?.total || 0),
                unit,
                sources: [...(nut?.sources || []), ...(sup?.sources || [])]
            };
        });

        return combined;
    },

    aggregateTrainingBurn(events: any[], dateStr: string): number {
        const dayEvents = events.filter(e => toLogicDate(e.start_time) === dateStr && (e.type === 'PERFORMANCE' || e.type === 'MOBILITY') && e.status === 'COMPLETED');
        return dayEvents.reduce((acc, curr) => acc + (Number((curr.preview_data as any)?.estimated_burn) || 0), 0);
    }
};
